import { Component, Show, createSignal } from "solid-js";

interface ShareModalProps {
	isOpen: boolean;
	onClose: () => void;
	onShareLink: () => Promise<string>; // Returns share URL
	onDownloadFile: () => void;
	isSharing: boolean;
}

const ShareModal: Component<ShareModalProps> = (props) => {
	const [shareUrl, setShareUrl] = createSignal("");
	const [copySuccess, setCopySuccess] = createSignal(false);
	const [error, setError] = createSignal(false);

	const handleShareLink = async () => {
		try {
			setError(false);
			const url = await props.onShareLink();
			setShareUrl(url);
			
			// Try to copy to clipboard
			try {
				await navigator.clipboard.writeText(url);
				setCopySuccess(true);
				setTimeout(() => setCopySuccess(false), 2000);
			} catch (clipboardErr) {
				console.warn('Failed to copy to clipboard:', clipboardErr);
			}
		} catch (err) {
			console.error('Failed to share:', err);
			setError(true);
		}
	};

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(shareUrl());
			setCopySuccess(true);
			setTimeout(() => setCopySuccess(false), 2000);
		} catch (err) {
			console.error("Failed to copy:", err);
		}
	};

	const handleClose = () => {
		setShareUrl("");
		setError(false);
		setCopySuccess(false);
		props.onClose();
	};

	return (
		<Show when={props.isOpen}>
			<div
				class="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]"
				onClick={handleClose}
			>
				<div
					class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
					onClick={(e) => e.stopPropagation()}
				>
					<div class="p-6">
						<h2 class="text-xl font-bold text-gray-900 mb-4">
							Dela scenario
						</h2>

						<Show when={shareUrl()}>
							{/* Show result after sharing link */}
							<>
								<p class="text-sm text-gray-600 mb-4">
									Scenariot har delats! Andra kan nu visa det via länken nedan.
								</p>

								<div class="mb-6">
									<label class="block text-sm font-medium text-gray-700 mb-2">
										Delningslänk
									</label>
									<div class="flex items-center gap-2">
										<input
											type="text"
											value={shareUrl()}
											readonly
											class="flex-1 px-3 py-2 border border-gray-300 rounded text-sm text-gray-700 bg-gray-50 font-mono"
											onClick={(e) => e.currentTarget.select()}
										/>
										<button
											onClick={handleCopy}
											class="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors flex items-center gap-1.5"
											title="Kopiera till urklipp"
										>
											<Show
												when={copySuccess()}
												fallback={
													<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
													</svg>
												}
											>
												<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
												</svg>
											</Show>
											<span class="text-sm font-medium">
												{copySuccess() ? "Kopierad!" : "Kopiera"}
											</span>
										</button>
									</div>
								</div>

								<div class="flex justify-end">
									<button
										onClick={handleClose}
										class="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
									>
										OK
									</button>
								</div>
							</>
						</Show>

						<Show when={!shareUrl() && !error()}>
							{/* Initial options */}
							<p class="text-sm text-gray-600 mb-6">
								Välj hur du vill dela scenariot:
							</p>

							<div class="space-y-3 mb-6">
								{/* Share Link */}
								<button
									onClick={handleShareLink}
									disabled={props.isSharing}
									class="w-full flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
								>
									<svg class="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
									</svg>
									<div class="flex-1">
										<div class="font-medium text-gray-900">
											{props.isSharing ? "Delar..." : "Skapa publik länk"}
										</div>
										<div class="text-sm text-gray-500 mt-1">
											Andra kan visa men inte ändra ditt scenario
										</div>
									</div>
								</button>

								{/* Download to Disk */}
								<button
									onClick={props.onDownloadFile}
									class="w-full flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
								>
									<svg class="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
									</svg>
									<div class="flex-1">
										<div class="font-medium text-gray-900">
											Spara till disk
										</div>
										<div class="text-sm text-gray-500 mt-1">
											Ladda ner som JSON-fil för säkerhetskopiering eller delning
										</div>
									</div>
								</button>
							</div>

							<div class="flex justify-end">
								<button
									onClick={handleClose}
									class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
								>
									Avbryt
								</button>
							</div>
						</Show>

						<Show when={error()}>
							{/* Error state */}
							<>
								<p class="text-sm text-red-600 mb-6">
									Det gick inte att dela scenariot. Försök igen.
								</p>
								<div class="flex justify-end gap-3">
									<button
										onClick={handleClose}
										class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
									>
										Avbryt
									</button>
									<button
										onClick={handleShareLink}
										disabled={props.isSharing}
										class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50"
									>
										Försök igen
									</button>
								</div>
							</>
						</Show>
					</div>
				</div>
			</div>
		</Show>
	);
};

export default ShareModal;
